/**
 * DNA采集点查询页面
 */

import { useState, useEffect } from 'react';
import type { DNACollectionPoint } from '../types';
import { getDNACollectionPoints, getNearbyDNAPoints, formatDistance } from '../services/antiTraffic';
import { getCurrentLocation } from '../services/location';

export default function DNAPage() {
  const [points, setPoints] = useState<DNACollectionPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);

  // 加载数据
  useEffect(() => {
    loadData();
    getUserLocation();
  }, []);

  // 获取用户位置
  const getUserLocation = async () => {
    try {
      const location = await getCurrentLocation(true, 5000);
      setUserLocation({ lat: location.latitude, lng: location.longitude });

      // 获取最近的采集点
      const nearby = await getNearbyDNAPoints(
        location.latitude,
        location.longitude,
        100 // 100公里范围内
      );
      if (nearby.length > 0) {
        setPoints(nearby);
      }
    } catch (error) {
      console.error('[DNA] 获取位置失败:', error);
      setLocationError('无法获取您的位置，请开启定位权限');
      // 加载所有采集点
      const allPoints = await getDNACollectionPoints();
      setPoints(allPoints);
    }
  };

  // 加载所有数据
  const loadData = async () => {
    setIsLoading(true);
    try {
      const allPoints = await getDNACollectionPoints();
      if (points.length === 0) {
        setPoints(allPoints);
      }
    } catch (error) {
      console.error('[DNA] 加载失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 导航到采集点
  const handleNavigate = (point: DNACollectionPoint) => {
    const url = `https://uri.amap.com/navigation?to=${point.longitude},${point.latitude},${point.name}&mode=car`;
    window.open(url, '_blank');
  };

  // 拨打电话
  const handleCall = (phone: string) => {
    window.location.href = `tel:${phone.replace(/-/g, '')}`;
  };

  return (
    <div className="min-h-screen bg-bg-dark">
      {/* 头部 */}
      <header className="bg-bg-light p-4">
        <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
          <span>🔬</span>
          <span>DNA采集点查询</span>
        </h1>
        <p className="text-sm text-text-muted mt-1">
          找到最近的DNA采集点，助力宝贝回家
        </p>

        {/* 位置状态 */}
        {locationError ? (
          <div className="mt-3 bg-warning/10 rounded-lg p-3">
            <p className="text-warning text-sm">⚠️ {locationError}</p>
          </div>
        ) : userLocation ? (
          <div className="mt-3 bg-success/10 rounded-lg p-3 flex items-center gap-2">
            <span className="text-success">📍</span>
            <span className="text-success text-sm">
              已定位，正在查找附近采集点...
            </span>
          </div>
        ) : (
          <div className="mt-3 bg-white/10 rounded-lg p-3 flex items-center gap-2">
            <span className="animate-spin">⏳</span>
            <span className="text-text-muted text-sm">
              正在获取您的位置...
            </span>
          </div>
        )}
      </header>

      {/* 说明卡片 */}
      <div className="p-4">
        <div className="bg-info/10 rounded-xl p-4">
          <h3 className="font-bold text-info mb-2">💡 DNA匹配是什么？</h3>
          <p className="text-sm text-text-muted">
            DNA比对是确认被拐儿童身份最准确的方法。如果您的孩子小时候走失，
            可以到就近的DNA采集点采集血样入库比对，帮助增加团圆机会。
          </p>
        </div>
      </div>

      {/* 列表 */}
      <main className="p-4 space-y-4">
        <h2 className="text-lg font-bold text-text-primary">
          最近的采集点
        </h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-sos-red border-t-transparent" />
          </div>
        ) : points.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-5xl mb-4">📍</div>
            <p className="text-text-muted">暂未找到附近的采集点</p>
          </div>
        ) : (
          <div className="space-y-3">
            {points.map(point => (
              <DNACard
                key={point.id}
                point={point}
                onNavigate={() => handleNavigate(point)}
                onCall={() => handleCall(point.phone)}
              />
            ))}
          </div>
        )}
      </main>

      {/* 底部提示 */}
      <div className="p-4 border-t border-white/10">
        <div className="bg-bg-light rounded-xl p-4">
          <h3 className="font-bold text-text-primary mb-2">📋 采集须知</h3>
          <ul className="text-sm text-text-muted space-y-2">
            <li>• 采血不需要空腹，正常饮食即可</li>
            <li>• 需携带身份证件</li>
            <li>• 如有失踪证明请一并携带</li>
            <li>• 比对结果通常需要数周时间</li>
            <li>• 咨询更多问题可拨打当地公安局电话</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// DNA采集点卡片
function DNACard({
  point,
  onNavigate,
  onCall
}: {
  point: DNACollectionPoint;
  onNavigate: () => void;
  onCall: () => void;
}) {
  return (
    <div className="bg-bg-light rounded-xl overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-bold text-text-primary">{point.name}</h3>
            {point.distance !== undefined && (
              <span className="text-sm text-success mt-1 inline-flex items-center gap-1">
                📍 距离您 {formatDistance(point.distance)}
              </span>
            )}
          </div>
          {point.is24Hour ? (
            <span className="px-2 py-0.5 rounded bg-success/20 text-success text-xs">
              24小时
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded bg-white/10 text-text-muted text-xs">
              {point.serviceHours}
            </span>
          )}
        </div>

        <div className="space-y-2 text-sm">
          <p className="text-text-muted flex items-start gap-2">
            <span>📍</span>
            <span>{point.address}</span>
          </p>
          <p className="text-text-muted flex items-center gap-2">
            <span>📞</span>
            <span>{point.phone}</span>
          </p>
        </div>

        {/* 所需证件 */}
        <div className="mt-3">
          <p className="text-xs text-text-muted mb-2">所需证件：</p>
          <div className="flex flex-wrap gap-2">
            {point.requiredDocs.map((doc, i) => (
              <span
                key={i}
                className="px-2 py-1 rounded bg-white/10 text-text-muted text-xs"
              >
                {doc}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="flex border-t border-white/10">
        <button
          onClick={onNavigate}
          className="flex-1 py-3 text-center text-sm text-info hover:bg-info/10 flex items-center justify-center gap-1"
        >
          <span>🗺️</span>
          <span>导航</span>
        </button>
        <button
          onClick={onCall}
          className="flex-1 py-3 text-center text-sm text-success hover:bg-success/10 flex items-center justify-center gap-1"
        >
          <span>📞</span>
          <span>拨打</span>
        </button>
      </div>
    </div>
  );
}
